import type { StoreCustomer } from "@hobbyshopify/backend-client";
import { useObserver, useSubtree } from "subtree.js/react";
import { CustomerTabs } from "../../components/CustomerTabs";
import { LoadFailed, Loading } from "../../components/LoadState";
import { ConfirmedScanRow, PendingScanRow } from "../../components/ScanRow";
import { UnrecognisedScan } from "../../components/UnrecognisedScan";
import { formatCents } from "../../core/primitives/card-display";
import { canAddToCart, cartLineForScan, cartLinesTotalCents } from "../../core/primitives/customer-cart";
import { customerInitial, customerLabel } from "../../core/primitives/customer-label";
import { formatDate } from "../../core/primitives/format";
import { CustomerQrModal } from "../../sections/customer-qr/customer-qr-modal";
import { CustomerScansActions, CustomerScansState, type NewCustomerDraft } from "./customer-scans-model";

function CustomerRow({ customer, onSelect }: { customer: StoreCustomer; onSelect: () => void }) {
  const label = customerLabel(customer);
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-center gap-3 rounded-row border border-line bg-surface p-4 text-left transition-colors hover:border-accent"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-strong text-sm font-semibold text-white">
        {customerInitial(label)}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-white">{label}</p>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-sm font-semibold text-white">
          {customer.scanCount} {customer.scanCount === 1 ? "scan" : "scans"}
        </p>
        <p className="text-xs text-faint">{formatDate(customer.lastScanAt)}</p>
      </div>
      <span className="shrink-0 text-lg text-faint">›</span>
    </button>
  );
}

function NewCustomerDialog({ draft }: { draft: NewCustomerDraft }) {
  const actions = useSubtree(CustomerScansActions);
  return (
    <div className="scrim items-end justify-center p-4 sm:items-center">
      <form
        className="modal w-full max-w-sm p-5"
        role="dialog"
        aria-modal="true"
        aria-label="Add customer"
        onSubmit={(e) => {
          e.preventDefault();
          void actions.createCustomer();
        }}
      >
        <h2 className="text-base font-semibold text-white">Add customer</h2>
        <p className="mt-1 text-xs text-muted">Someone at the counter. You scan and charge for them.</p>
        <label className="label mt-4" htmlFor="new-customer-name">
          Name
        </label>
        <input
          id="new-customer-name"
          className={`field ${draft.error ? "field-error" : ""}`}
          value={draft.name}
          onChange={(e) => actions.setNewCustomerName(e.target.value)}
          autoComplete="off"
          // biome-ignore lint/a11y/noAutofocus: the dialog has one field and opens to type into it.
          autoFocus
        />
        {draft.error && <p className="mt-1 text-xs text-danger">{draft.error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={actions.closeNewCustomer} className="btn-secondary btn-sm">
            Cancel
          </button>
          <button type="submit" disabled={draft.busy} className="btn-primary btn-sm">
            {draft.busy ? "Adding…" : "Start sale"}
          </button>
        </div>
      </form>
    </div>
  );
}

export function CustomerScansPage() {
  const state = useSubtree(CustomerScansState);
  const actions = useSubtree(CustomerScansActions);
  const observer = useObserver();

  const selectedCustomerId = state.customerId;
  const storeId = observer.watch(state.storeId);
  const customersStatus = observer.watch(state.customersStatus);
  const customers = observer.watch(state.customers);
  const selectedCustomer = observer.watch(state.selectedCustomer);
  const scansStatus = observer.watch(state.scansStatus);
  const customerScans = observer.watch(state.scans);
  const openCarts = observer.watch(state.openCarts);
  const cart = observer.watch(state.cart);
  const cartStatus = observer.watch(state.cartStatus);
  const busyScanId = observer.watch(state.busyScanId);
  const actionError = observer.watch(state.actionError);
  const newCustomer = observer.watch(state.newCustomer);
  const carts = cart ? [cart] : [];

  const pending = (kind: string) => kind === "idle" || kind === "loading";
  const loading =
    pending(customersStatus.kind) || (selectedCustomerId !== null && pending(scansStatus.kind)) || storeId === null;
  const error = customersStatus.kind === "error" || (selectedCustomerId !== null && scansStatus.kind === "error");

  const renderContent = () => {
    if (error) {
      const failed = customersStatus.kind === "error" ? customersStatus : scansStatus;
      return (
        <LoadFailed
          title={selectedCustomerId ? "Couldn't load this customer's scans." : "Couldn't load your customers."}
          message={failed.kind === "error" ? failed.message : null}
          onRetry={actions.retry}
        />
      );
    }
    if (loading) return <Loading />;

    // Drilled into one customer: their scans, pending ones reviewable in place.
    if (selectedCustomerId) {
      if (customerScans.length === 0) {
        return (
          <div className="py-10 text-center">
            <p className="text-sm text-muted">
              {selectedCustomer?.isPos ? "Scan this customer's cards." : "This customer has no valid scans."}
            </p>
            {selectedCustomer?.isPos && (
              <button type="button" onClick={actions.scanForCustomer} className="btn-primary btn-sm mt-4">
                Scan cards
              </button>
            )}
          </div>
        );
      }
      return (
        <div className="space-y-2">
          {customerScans.map((s) =>
            s.recognitionOutcome === "no_match" ? (
              <UnrecognisedScan key={s.id} scan={s} />
            ) : s.review === "PENDING" ? (
              <PendingScanRow key={s.id} scan={s} onReview={() => actions.editScan(s)} />
            ) : (
              <ConfirmedScanRow
                key={s.id}
                scan={s}
                onEdit={() => actions.editScan(s)}
                cart={{
                  inCart: cartLineForScan(carts, s) !== null,
                  canAdd: canAddToCart(s) && cartStatus.kind === "loaded",
                  busy: busyScanId === s.id,
                  onAdd: () => void actions.addToCart(s),
                  onRemove: () => void actions.removeFromCart(s),
                }}
              />
            ),
          )}
        </div>
      );
    }

    // Customer list.
    if (customers.length === 0) {
      return (
        <p className="py-10 text-center text-sm text-muted">
          No customers yet. They appear here once they scan from the Shop QR, or add one at the counter.
        </p>
      );
    }
    return (
      <div className="space-y-2">
        {customers.map((c) => (
          <CustomerRow key={c.id} customer={c} onSelect={() => actions.selectCustomer(c.id)} />
        ))}
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-7xl px-2 py-4 sm:px-6 sm:py-8 lg:px-8">
      <div className="panel p-4 sm:p-6">
        <CustomerTabs active="scans" openCarts={openCarts} onShowQr={actions.showQr} qrDisabled={!storeId} />

        <CustomerQrModal />

        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          {selectedCustomerId ? (
            <button
              type="button"
              onClick={actions.showAllCustomers}
              className="inline-flex min-w-0 items-center gap-1 text-sm font-medium text-muted transition-colors hover:text-white"
            >
              ‹ All customers
              {selectedCustomer && (
                <span className="ml-1 truncate text-white">/ {customerLabel(selectedCustomer)}</span>
              )}
            </button>
          ) : (
            <span />
          )}
          {selectedCustomerId ? (
            selectedCustomer?.isPos &&
            customerScans.length > 0 && (
              <button type="button" onClick={actions.scanForCustomer} className="btn-secondary btn-sm">
                Scan cards
              </button>
            )
          ) : (
            <button type="button" onClick={actions.openNewCustomer} disabled={!storeId} className="btn-primary btn-sm">
              Add customer
            </button>
          )}
        </div>

        {actionError && (
          <div className="note note-danger mb-4 flex items-center justify-between gap-3 text-danger" role="alert">
            <span className="min-w-0">{actionError}</span>
            <button type="button" onClick={actions.dismissActionError} className="btn-ghost shrink-0">
              Dismiss
            </button>
          </div>
        )}

        {selectedCustomerId && cartStatus.kind === "error" && (
          <LoadFailed
            title="Couldn't load this customer's cart."
            message={cartStatus.message}
            onRetry={actions.retry}
          />
        )}

        {renderContent()}
      </div>

      {selectedCustomerId && cart && cart.items.length > 0 && (
        // Sticky, so the sale can be closed from wherever the list is scrolled to.
        <div className="sticky bottom-0 z-10 mt-4 border-t border-line bg-app-elevated/95 py-3 backdrop-blur">
          <button type="button" onClick={actions.checkout} className="btn-primary w-full">
            Checkout · {formatCents(cartLinesTotalCents(cart))}
            <span className="ml-1 font-normal opacity-80">
              ({cart.items.length} {cart.items.length === 1 ? "card" : "cards"})
            </span>
          </button>
        </div>
      )}

      {newCustomer && <NewCustomerDialog draft={newCustomer} />}
    </div>
  );
}
